import { supabase } from "./supabase";

// ─────────────────────────────────────────────────────────────────────────────
// Pending + released XP ledger (Phase 4, round 5 wave C — client wiring).
//
// Wave A's server check-in writes rows into `xp_ledger` with status 'pending';
// they flip to 'released' when an admin confirms the round and releases it
// (Phase 4). The client shows the pending sum ADDITIVELY — it is NOT folded
// into the transitional client-side total (calcPointsBreakdown stays the
// source of truth for the big number until the XP-migration wave lands).
//
// RLS scopes the select to the signed-in user's own rows, so this is safe to
// call from the browser. The UI hides on any error and never crashes.
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_SUMMARY = () => ({
  pending: { total: 0, count: 0 },
  released: { total: 0, count: 0 },
});

/**
 * Fetch the signed-in user's pending (not-yet-released) server-verified XP.
 *
 * @returns {Promise<{total: number, count: number}>} sum of pending ledger
 *   amounts and the row count; `{ total: 0, count: 0 }` on error (with a
 *   console.warn — the UI hides, never crashes).
 */
export async function fetchPendingXp(userId) {
  const summary = await fetchXpLedgerSummary(userId);
  return summary.pending;
}

/**
 * Fetch a pending + released summary of the signed-in user's own xp_ledger
 * rows in a single select. Returns `{ pending:{total,count}, released:{total,count} }`;
 * `{0,0}` on both halves on error (with a console.warn — the UI hides, never
 * crashes). Display-only; the transitional client-side total
 * (calcPointsBreakdown) stays the big number until the XP-migration wave.
 */
export async function fetchXpLedgerSummary(userId) {
  if (!userId) return EMPTY_SUMMARY();
  const { data, error } = await supabase
    .from("xp_ledger")
    .select("amount,status")
    .eq("user_id", userId);

  if (error) {
    console.warn("[xpLedger] could not load XP summary:", error.message);
    return EMPTY_SUMMARY();
  }

  const out = EMPTY_SUMMARY();
  const rows = Array.isArray(data) ? data : [];
  for (const r of rows) {
    const amount = Number(r?.amount || 0);
    const bucket = r?.status === "released" ? out.released : out.pending;
    bucket.total += amount;
    bucket.count += 1;
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Round 9 — XP on server truth. The Home "YOUR XP" figure and its breakdown
// modal come from the member's released ledger rows for the current season
// (one number with the leaderboard). `fetchMyXp` reads `get_my_xp(p_season)`
// (SECURITY DEFINER, RLS-owner); `deriveXpSections` is a pure mapper from the
// released rows to the prototype's breakdown-section shape (colour token NAMES
// so the module stays UI-free — App.jsx maps them to T). Pending stays a
// separate line. The client never writes reward state (SEC-3).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Map one `get_my_xp` row (snake_case jsonb) to the camelCase shape the UI
 * consumes. PURE; no Supabase.
 */
function mapXpRow(r) {
  const fx = r && r.fixture && typeof r.fixture === "object" ? r.fixture : null;
  return {
    id: r?.id ?? null,
    sourceType: r?.source_type ?? null,
    status: r?.status ?? null,
    amount: Number(r?.amount || 0),
    label: r?.label ?? "",
    fixture: fx
      ? {
          round: fx.round ?? null,
          matchDate: fx.match_date ?? null,
          homeRef: fx.home_ref ?? null,
          awayRef: fx.away_ref ?? null,
        }
      : null,
    createdAt: r?.created_at ?? null,
    releasedAt: r?.released_at ?? null,
  };
}

/**
 * Fetch the signed-in user's own XP ledger for a season from `get_my_xp`.
 * Returns `{ seasonYear, releasedTotal, pendingTotal, rows, error: false }`
 * (rows camelCased, newest-first as the server returns them) or, on any error
 * or throw, `{ seasonYear: null, releasedTotal: 0, pendingTotal: 0, rows: [],
 * error: true }` (console.warn, never throws). Display-only.
 *
 * @param {number} seasonYear
 */
export async function fetchMyXp(seasonYear) {
  try {
    const { data, error } = await supabase.rpc("get_my_xp", { p_season: seasonYear });
    if (error) throw error;
    const payload = data && typeof data === "object" ? data : {};
    const rows = Array.isArray(payload.rows) ? payload.rows.map(mapXpRow) : [];
    return {
      seasonYear: payload.season_year ?? seasonYear ?? null,
      releasedTotal: Number(payload.released_total || 0),
      pendingTotal: Number(payload.pending_total || 0),
      rows,
      error: false,
    };
  } catch (err) {
    console.warn("[xpLedger] could not load my XP:", err?.message || err);
    return { seasonYear: null, releasedTotal: 0, pendingTotal: 0, rows: [], error: true };
  }
}

/**
 * Derive the prototype's breakdown sections from RELEASED ledger rows only.
 * PURE: no Supabase, no mutation of the input. Returns
 * `[{ key, label, pts, color, icon, rows: [{ label, pts, isNew }] }]` —
 * "Teams Seen" (key teams, 👕, colour token "green"; one row per team label,
 * pts summed, isNew when any team_first), "Venues Visited" (venues, 🏟️,
 * "blue"), "Players Seen" (players, 👤, "gold"; one row `${n} unique players`
 * where n = distinct labels, pts summed), "Badges Earned" (badges, 🏅,
 * "purpleLight"; one row per badge label), "Forever Sets" (forever, 🗃️,
 * "purple"; one row per submission label, e.g. "Common Set (Forever)" as
 * labelled by get_my_xp). Unknown source_types are ignored;
 * each section's rows are sorted pts desc; sections with pts 0 are omitted.
 * `color` is a token NAME so the module stays UI-free (App.jsx maps to T).
 *
 * @param {Array<{id,sourceType,status,amount,label}>} rows
 */
export function deriveXpSections(rows) {
  const released = (Array.isArray(rows) ? rows : []).filter(
    (r) => r && r.status === "released",
  );
  const teams = new Map();
  const venues = new Map();
  const players = new Map();
  const badges = new Map();
  const forever = new Map();
  for (const r of released) {
    const amt = Number(r?.amount || 0);
    const label = r?.label ?? "";
    switch (r?.sourceType) {
      case "team_first":
      case "team_repeat": {
        const e = teams.get(label) || { pts: 0, isNew: false };
        e.pts += amt;
        if (r.sourceType === "team_first") e.isNew = true;
        teams.set(label, e);
        break;
      }
      case "venue_first":
      case "venue_repeat": {
        const e = venues.get(label) || { pts: 0, isNew: false };
        e.pts += amt;
        if (r.sourceType === "venue_first") e.isNew = true;
        venues.set(label, e);
        break;
      }
      case "player_first":
      case "player_repeat": {
        const e = players.get(label) || { pts: 0 };
        e.pts += amt;
        players.set(label, e);
        break;
      }
      case "badge": {
        const e = badges.get(label) || { pts: 0 };
        e.pts += amt;
        badges.set(label, e);
        break;
      }
      case "forever": {
        const e = forever.get(label) || { pts: 0 };
        e.pts += amt;
        forever.set(label, e);
        break;
      }
      default:
        break;
    }
  }
  const byPtsDesc = (a, b) => b.pts - a.pts;
  const teamRows = [...teams.entries()]
    .map(([label, e]) => ({ label, pts: e.pts, isNew: e.isNew }))
    .sort(byPtsDesc);
  const venueRows = [...venues.entries()]
    .map(([label, e]) => ({ label, pts: e.pts, isNew: e.isNew }))
    .sort(byPtsDesc);
  const playerPts = [...players.values()].reduce((s, e) => s + e.pts, 0);
  const playerRows =
    players.size > 0
      ? [{ label: `${players.size} unique players`, pts: playerPts, isNew: false }]
      : [];
  const badgeRows = [...badges.entries()]
    .map(([label, e]) => ({ label, pts: e.pts }))
    .sort(byPtsDesc);
  const foreverRows = [...forever.entries()]
    .map(([label, e]) => ({ label, pts: e.pts }))
    .sort(byPtsDesc);
  const sections = [
    {
      key: "teams",
      label: "Teams Seen",
      pts: teamRows.reduce((s, r) => s + r.pts, 0),
      color: "green",
      icon: "👕",
      rows: teamRows,
    },
    {
      key: "venues",
      label: "Venues Visited",
      pts: venueRows.reduce((s, r) => s + r.pts, 0),
      color: "blue",
      icon: "🏟️",
      rows: venueRows,
    },
    {
      key: "players",
      label: "Players Seen",
      pts: playerPts,
      color: "gold",
      icon: "👤",
      rows: playerRows,
    },
    {
      key: "badges",
      label: "Badges Earned",
      pts: badgeRows.reduce((s, r) => s + r.pts, 0),
      color: "purpleLight",
      icon: "🏅",
      rows: badgeRows,
    },
    {
      key: "forever",
      label: "Forever Sets",
      pts: foreverRows.reduce((s, r) => s + r.pts, 0),
      color: "purple",
      icon: "🗃️",
      rows: foreverRows,
    },
  ];
  return sections.filter((s) => s.pts > 0);
}
